<script lang="ts" setup>
import { LockOutlined, UserOutlined } from '@ant-design/icons-vue'
import { toTypedSchema } from '@vee-validate/zod'
import { message } from 'ant-design-vue'
import { useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { z } from 'zod'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()
const authError = ref<string | null>(null)

type LoginForm = {
  email: string
  password: string
  remember: boolean
}

const schema = z.object({
  email: z.string().nonempty('Email is required').email('Please input a valid email!'),
  password: z
    .string()
    .nonempty('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  remember: z.boolean().optional(),
})

const { handleSubmit, errors } = useForm<LoginForm>({
  validationSchema: toTypedSchema(schema as any),
  initialValues: { email: '', password: '', remember: true },
})

const { value: email } = useField<string>('email')
const { value: password } = useField<string>('password')
const { value: remember } = useField<boolean>('remember')

const loading = ref(false)

const onSubmit = handleSubmit(async (values) => {
  try {
    loading.value = true
    authError.value = null
    await auth.login(values.email, values.password, values.remember)
    message.success('Logged in successfully!')
  } catch (err) {
    if (
      err instanceof Error &&
      (err.message === 'Your account is pending approval.' ||
        err.message === 'Please verify your email before continuing.')
    ) {
      authError.value = null
      return
    }
    if (err instanceof Error && err.message) {
      authError.value = err.message
      console.error('Login error:', err)
    } else if (typeof err === 'string' && err.trim()) {
      authError.value = err
      console.error('Login error:', err)
    } else {
      authError.value = 'Invalid username or password.'
      console.error('Login error: unknown error', err)
    }
  } finally {
    loading.value = false
  }
})

const disabled = computed(() => !email.value || !password.value)
</script>
<template>
  <div class="login-container">
    <a-card class="login-form" title="Login">
      <a-form
        name="normal_login"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        @submit.prevent="onSubmit"
      >
        <a-form-item
          label="Email"
          :validate-status="errors.email ? 'error' : ''"
          :help="errors.email"
        >
          <a-input v-model:value="email" autocomplete="email">
            <template #prefix>
              <UserOutlined class="site-form-item-icon" />
            </template>
          </a-input>
        </a-form-item>

        <a-form-item
          label="Password"
          :validate-status="errors.password ? 'error' : ''"
          :help="errors.password"
        >
          <a-input-password v-model:value="password" autocomplete="current-password">
            <template #prefix>
              <LockOutlined class="site-form-item-icon" />
            </template>
          </a-input-password>
        </a-form-item>

        <a-form-item :wrapper-col="{ span: 24 }">
          <a-flex justify="space-between" align="center">
            <a-form-item name="remember" no-style>
              <a-checkbox v-model:checked="remember">Remember me</a-checkbox>
            </a-form-item>
            <router-link to="/forgot-password">Forgot password</router-link>
          </a-flex>
        </a-form-item>

        <a-form-item v-if="authError" :wrapper-col="{ span: 24 }">
          <a-alert :message="authError" type="error" show-icon />
        </a-form-item>

        <a-form-item :wrapper-col="{ span: 24 }">
          <a-button
            :disabled="disabled"
            :loading="loading"
            type="primary"
            html-type="submit"
            class="login-form-button"
            block
          >
            Log in
          </a-button>
        </a-form-item>

        <a-form-item :wrapper-col="{ span: 24 }">
          <div style="text-align: center">
            Or
            <router-link to="/register">register now!</router-link>
          </div>
        </a-form-item>
      </a-form>
    </a-card>
  </div>
</template>
<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 85vh;
}

.login-form {
  max-width: 350px;
}
</style>
